<template>
  <div class="grain"></div>

  <nav id="navbar" :class="{ scrolled: isScrolled }">
    <div class="nav-logo">
      <a href="/"><img alt="Villa Proistakis" src="/logo.png" /></a>
    </div>
    <div class="nav-links desk">
      <router-link to="/">Home</router-link>
      <router-link to="/our-villas">Our Villas</router-link>
      <router-link to="/discover">Discover</router-link>
      <router-link to="/contact">Contact</router-link>
      <a href="https://proistakisvillas.reserve-online.net" class="nav-book" target="_blank" rel="noopener">Book a stay</a>
    </div>
    <button class="mobile-toggle" @click="openNav" aria-label="Open menu" v-show="!navOpen">
      <span></span><span></span><span></span>
    </button>
  </nav>

  <div class="mobile-nav" :class="{ open: navOpen }">
    <div class="mobile-nav-top">
      <a href="/" @click="openNav"><img src="/logo.png" alt="Villa Proistakis" /></a>
      <button class="mobile-close" @click="openNav" aria-label="Close menu">
        <span></span><span></span>
      </button>
    </div>
    <nav class="mobile-nav-links">
      <router-link to="/" @click="openNav">Home</router-link>
      <router-link to="/our-villas" @click="openNav">Our Villas</router-link>
      <router-link to="/discover" @click="openNav">Discover</router-link>
      <router-link to="/contact" @click="openNav">Contact</router-link>
    </nav>
    <div class="mobile-nav-bottom">
      <a href="https://proistakisvillas.reserve-online.net" class="btn btn-gold" target="_blank" rel="noopener" @click="openNav">Book a stay</a>
      <span>Ierapetra · Crete · Greece</span>
    </div>
  </div>

  <main v-if="!navOpen">
    <router-view v-slot="{ Component }">
      <Transition name="page" mode="out-in">
        <component :is="Component" :key="$route.path" />
      </Transition>
    </router-view>
  </main>

  <footer class="site-footer" v-if="!navOpen">
    <div class="footer-top">
      <div class="footer-brand">
        <img alt="Villa Proistakis" src="/logo.png" />
        <p>Private luxury villas on the southern coast of Crete, Greece.</p>
      </div>
      <div class="footer-cols">
        <div class="footer-col">
          <span class="footer-label">Pages</span>
          <router-link to="/">Home</router-link>
          <router-link to="/our-villas">Our Villas</router-link>
          <router-link to="/discover">Discover</router-link>
          <router-link to="/contact">Contact</router-link>
        </div>
        <div class="footer-col">
          <span class="footer-label">Contact</span>
          <span>Ierapetra, Lasithi, Crete</span>
          <a :href="'tel:' + info.telephone">{{ info.telephone }}</a>
          <a :href="'mailto:' + info.email">{{ info.email }}</a>
        </div>
        <div class="footer-col">
          <span class="footer-label">Follow</span>
          <a :href="info.social.instagram" target="_blank" rel="noopener">Instagram</a>
          <a :href="info.social.facebook" target="_blank" rel="noopener">Facebook</a>
          <a :href="info.social.tiktok" target="_blank" rel="noopener">TikTok</a>
        </div>
      </div>
    </div>
    <div class="footer-bottom">
      <span>© {{ new Date().getFullYear() }} Villa Proistakis</span>
      <span>All rights reserved</span>
    </div>
  </footer>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'App',
  data() {
    return {
      isScrolled: false,
      navOpen: false,
      info: {
        location: 'Villa Proistakis, Ierapetra, Lasithi, Crete, Greece',
        email: 'proist@otenet.gr',
        telephone: '+30 694 555 3178',
        social: {
          facebook: 'https://www.facebook.com/people/Villa-proistakis/100063573460991/',
          instagram: 'https://www.instagram.com/villa_proistakis',
          tiktok: 'https://www.tiktok.com/@villaproistakis'
        }
      },
      villas: [
        {
          name: 'mystique_luxury_suite',
          label: 'Mystique Luxury Suite',
          info: { bedrooms: 2, bathrooms: 2, beachfront: true },
          preview_image: 1,
          photos_num: 33,
          description: 'Nestled in the tranquil village of Achlia, Mystique Luxury Suite offers an exclusive retreat for those seeking refined elegance and ultimate relaxation. This chic villa features a rooftop pool, free WiFi, and private parking, providing guests with modern comforts amidst a picturesque setting. With air-conditioned accommodation, a balcony, and access to a hot tub, hammam, and steam room, every detail is crafted for indulgence. The villa\'s two bedrooms, sea-view terrace, and well-appointed kitchen ensure a seamless blend of luxury and convenience.',
          mini_description: 'In Achlia, Mystique Luxury Suite offers a cozy retreat with a rooftop pool and free WiFi. This air-conditioned villa features a balcony, hot tub, and hammam. Two bedrooms, sea views, and modern amenities - just steps from Achlia Beach.',
          features: { whole_villa: true, size: 130, wifi: true, view: true, kitchen_equipment: true, balcony: true, garden: true, hydromassage: true, pool: true }
        },
        {
          name: 'villa_proistakis',
          label: 'Villa Proistakis',
          info: { bedrooms: '4–6', bathrooms: 1, beachfront: true },
          preview_image: 2,
          photos_num: 20,
          description: 'Nestled near Achlia Beach, Villa Proistakis offers air-conditioned rooms with private bathrooms and scenic views. Guests enjoy free parking and a paid airport shuttle. Apartments feature sea-view patios, fully equipped kitchens, and free WiFi. The complex includes barbecue facilities, and activities like cycling and fishing are nearby.',
          mini_description: 'Villa Proistakis offers air-conditioned rooms near Achlia Beach with free parking. Apartments include sea-view patios, kitchens, and free WiFi. On-site barbecue with nearby cycling and fishing.',
          features: { whole_villa: true, size: '40–80', wifi: true, view: true, kitchen_equipment: true, balcony: true, garden: true, hydromassage: false, pool: true }
        },
        {
          name: 'bay_view_residence',
          label: 'Bay View Residence',
          info: { bedrooms: 2, bathrooms: 1, beachfront: true },
          preview_image: 1,
          photos_num: 21,
          description: 'Perched along the scenic coastline, Bay View Residence offers a serene beachfront escape just 1.5 km from Beach Maheridi. With panoramic sea views, a private balcony, and terrace, guests can immerse themselves in seaside tranquility. The two-bedroom holiday home features free WiFi, air conditioning, and a fully equipped kitchen.',
          mini_description: 'Bay View Residence, 1.5 km from Beach Maheridi, offers panoramic sea views, a garden, and a balcony. Two bedrooms, full kitchen, and WiFi - ideal for a seaside getaway.',
          features: { whole_villa: false, size: 60, wifi: true, view: true, kitchen_equipment: true, balcony: true, garden: true, hydromassage: false, pool: false }
        },
        {
          name: 'chelidonofolia',
          label: 'Chelidonofolia',
          info: { bedrooms: '1–2', bathrooms: 1, beachfront: false },
          preview_image: 1,
          photos_num: 17,
          description: 'Nestled amidst stunning mountain vistas, Chelidonofolia offers a serene retreat 42 km from Lake Voulismeni. Air-conditioned rooms, private parking, and free WiFi. Each unit boasts a terrace with sea views, dining area, and well-equipped kitchen. Guests can unwind in the tranquil garden surroundings.',
          mini_description: 'Chelidonofolia: tranquil mountain views, 42 km from Lake Voulismeni. Air-conditioned rooms, free parking and WiFi. Terrace with sea views and a full kitchen. Peaceful Cretan garden.',
          features: { whole_villa: false, size: 60, wifi: true, view: true, kitchen_equipment: true, balcony: true, garden: true, hydromassage: false, pool: false }
        },
        {
          name: 'studio_summer_vibes',
          label: 'Studio Summer Vibes',
          info: { bedrooms: 1, bathrooms: 1, beachfront: true },
          preview_image: 1,
          photos_num: 12,
          description: 'Located in Achlia, Summer Vibes sits steps away from Achlia Beach. Garden, barbecue, and bicycle parking on-site. Air-conditioned units with sea views, picnic areas, and free WiFi. Full kitchen, flat-screen TV, and outdoor dining on the patio. Bicycle rental available.',
          mini_description: 'Summer Vibes in Achlia - steps from the beach. Garden, barbecue, bicycle parking. Air-conditioned with sea views, free WiFi, and a full kitchen.',
          features: { whole_villa: false, size: 40, wifi: true, view: true, kitchen_equipment: true, balcony: true, garden: true, hydromassage: false, pool: false }
        },
        {
          name: 'luxury_villa_asana',
          label: 'Luxury Villa Asana',
          info: { bedrooms: 3, bathrooms: 3, beachfront: true },
          preview_image: 1,
          photos_num: 12,
          description: 'Luxury Villa Asana in Koutsouras offers a serene escape with free WiFi, air conditioning, an outdoor pool, and a lush garden. Just 400 m from Koutsouras Beach. Three bedrooms, three bathrooms, spacious living area, and a fully-equipped kitchen. Terrace and free private parking included.',
          mini_description: 'Luxury Villa Asana in Koutsouras - outdoor pool, garden, and 400 m from the beach. Three bedrooms, terrace, and private parking. Sitia Airport 38 km away.',
          features: { whole_villa: true, size: 140, wifi: true, view: true, kitchen_equipment: true, balcony: true, garden: false, hydromassage: false, pool: true }
        },
      ]
    };
  },
  methods: {
    getInfo()  { return this.info; },
    getVillas() { return this.villas; },
    getVilla(id: string) { return this.villas.find(v => v.name === id); },
    getImagePath(villa: string, image: number) { return `/villas_photos/${villa}/image-${image}.webp`; },
    openNav() { this.navOpen = !this.navOpen; },
    redirect(url: string) { window.location.href = url; }
  },
  mounted() {
    window.addEventListener('scroll', () => {
      this.isScrolled = window.scrollY > 40;
    });
  }
});
</script>

<style>
:root {
  --bg:       #0C0A08;
  --layer:    #141109;
  --layer2:   #1C1914;
  --text:     #EDE6D8;
  --muted:    #7A7060;
  --gold:     #C9A96C;
  --gold2:    #E2C999;
  --line:     rgba(201,169,108,0.14);
  --white:    #FFFFFF;

  --serif:    'Cormorant Garamond', Georgia, serif;
  --sans:     'Outfit', system-ui, sans-serif;

  --ease:     cubic-bezier(0.4, 0, 0.2, 1);
  --spring:   cubic-bezier(0.34, 1.56, 0.64, 1);
  --t:        0.3s;
  --max-w:    1400px;
  --z-nav:    100;
}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

html { scroll-behavior: smooth; }

body {
  background: var(--bg);
  color: var(--text);
  font-family: var(--sans);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  overflow-x: hidden;
}

img    { display: block; max-width: 100%; }
a      { color: inherit; text-decoration: none; }
button { font-family: inherit; cursor: pointer; }

.grain {
  position: fixed;
  inset: 0;
  z-index: 9998;
  pointer-events: none;
  opacity: 0.038;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size: 180px;
}

#navbar {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: var(--z-nav);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2.5rem;
  height: 76px;
  transition: background var(--t) var(--ease),
              backdrop-filter var(--t) var(--ease),
              border-color var(--t) var(--ease);
  border-bottom: 1px solid transparent;
}

#navbar.scrolled {
  background: rgba(12, 10, 8, 0.88);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-color: var(--line);
}

.nav-logo img {
  height: 50px;
  width: auto;
  filter: brightness(1.05);
}

.nav-links.desk {
  display: flex;
  align-items: center;
  gap: 2.5rem;
}

.nav-links.desk a {
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(237,230,216,0.75);
  position: relative;
  padding-bottom: 2px;
  transition: color var(--t);
}

.nav-links.desk a::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0; right: 0;
  height: 1px;
  background: var(--gold);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform var(--t) var(--ease);
}

.nav-links.desk a:hover,
.nav-links.desk a.router-link-active { color: var(--text); }

.nav-links.desk a:hover::after,
.nav-links.desk a.router-link-active::after { transform: scaleX(1); }

.nav-book {
  font-size: 0.72rem !important;
  letter-spacing: 0.12em !important;
  padding: 9px 20px !important;
  border: 1px solid var(--gold) !important;
  border-radius: 2px;
  color: var(--gold) !important;
  transition: background var(--t), color var(--t) !important;
}

.nav-book::after { display: none !important; }

.nav-book:hover {
  background: var(--gold) !important;
  color: var(--bg) !important;
}

/* hamburger */
.mobile-toggle {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
}

.mobile-toggle span {
  display: block;
  width: 24px;
  height: 1.5px;
  background: var(--text);
  transition: transform var(--t), opacity var(--t);
}

.mobile-toggle span.hide { opacity: 0; }

.mobile-nav {
  display: none;
  position: fixed;
  inset: 0;
  z-index: var(--z-nav);
  background: var(--bg);
  flex-direction: column;
}

.mobile-nav.open { display: flex; }

/* top bar - logo + close */
.mobile-nav-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
  height: 76px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--line);
}

.mobile-nav-top img {
  height: 44px;
  width: auto;
}

.mobile-close {
  background: none;
  border: none;
  width: 36px;
  height: 36px;
  position: relative;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}

.mobile-close span {
  display: block;
  position: absolute;
  width: 22px;
  height: 1.5px;
  background: var(--text);
  top: 50%;
  left: 50%;
}

.mobile-close span:nth-child(1) { transform: translate(-50%, -50%) rotate(45deg); }
.mobile-close span:nth-child(2) { transform: translate(-50%, -50%) rotate(-45deg); }

/* links */
.mobile-nav-links {
  display: flex;
  flex-direction: column;
  padding: 2.5rem 1.75rem 0;
  flex: 1;
}

.mobile-nav-links a {
  font-family: var(--serif);
  font-size: clamp(2.6rem, 11vw, 4.5rem);
  font-weight: 300;
  color: var(--text);
  letter-spacing: -0.02em;
  line-height: 1.2;
  text-decoration: none;
  padding: 0.15em 0;
  border-bottom: 1px solid var(--line);
  opacity: 0.45;
  transition: opacity 0.2s ease, color 0.2s ease;
}

.mobile-nav-links a:first-child { border-top: 1px solid var(--line); }

.mobile-nav-links a:hover,
.mobile-nav-links a.router-link-active {
  opacity: 1;
  color: var(--gold);
}

/* bottom */
.mobile-nav-bottom {
  padding: 2rem 1.75rem 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  border-top: 1px solid var(--line);
  flex-shrink: 0;

  span {
    font-size: 0.68rem;
    font-weight: 500;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--muted);
  }

  .btn { align-self: flex-start; }
}

.page-header {
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  min-height: 55vh;
  padding: 0 2.5rem 4rem;
  overflow: hidden;
}

.page-header-bg {
  position: absolute;
  inset: -15%;
  background-image: url(/public/general-photo-1.webp);
  background-size: cover;
  background-position: center 60%;
  filter: brightness(0.4);
  z-index: 0;
  will-change: transform;
  animation: headerZoom 8s var(--ease) forwards;
}

@keyframes headerZoom {
  from { transform: translateY(0) scale(1.04); }
  to   { transform: translateY(0) scale(1); }
}

.page-enter-active {
  transition: opacity 0.45s cubic-bezier(0.4,0,0.2,1),
              transform 0.45s cubic-bezier(0.4,0,0.2,1);
}
.page-leave-active {
  transition: opacity 0.25s cubic-bezier(0.4,0,0.2,1),
              transform 0.25s cubic-bezier(0.4,0,0.2,1);
}
.page-enter-from {
  opacity: 0;
  transform: translateY(18px);
}
.page-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.page-header-title {
  position: relative;
  z-index: 1;
  font-family: var(--serif);
  font-size: clamp(3rem, 7vw, 6rem);
  font-weight: 300;
  color: var(--text);
  letter-spacing: -0.01em;
  line-height: 1;
}

.page-header-eyebrow {
  position: relative;
  z-index: 1;
  display: block;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--gold);
  margin-bottom: 0.8rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--sans);
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  padding: 14px 28px;
  border-radius: 2px;
  cursor: pointer;
  transition: background var(--t) var(--ease),
              color var(--t) var(--ease),
              border-color var(--t) var(--ease),
              transform var(--t) var(--ease);
  border: none;
}

.btn-gold {
  background: var(--gold);
  color: var(--bg);
}
.btn-gold:hover {
  background: var(--gold2);
  transform: translateY(-1px);
}

.btn-outline-gold {
  background: transparent;
  color: var(--gold);
  border: 1px solid var(--gold);
}
.btn-outline-gold:hover {
  background: var(--gold);
  color: var(--bg);
}

.btn-outline-white {
  background: transparent;
  color: var(--text);
  border: 1px solid rgba(237,230,216,0.4);
}
.btn-outline-white:hover {
  border-color: var(--text);
  background: rgba(237,230,216,0.06);
}

.text-link {
  font-size: 0.8rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--gold);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: gap var(--t) var(--ease), color var(--t);
}
.text-link:hover { gap: 0.8rem; color: var(--gold2); }

.eyebrow {
  display: block;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--gold);
  margin-bottom: 1rem;
}

.site-footer {
  background: var(--layer);
  border-top: 1px solid var(--line);
  padding: 5rem 2.5rem 0;
}

.footer-top {
  max-width: var(--max-w);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.6fr 2fr;
  gap: 4rem;
  padding-bottom: 4rem;
  border-bottom: 1px solid var(--line);
}

.footer-brand img {
  height: 50px;
  width: auto;
  margin-bottom: 1.2rem;
}

.footer-brand p {
  font-size: 0.88rem;
  line-height: 1.65;
  color: var(--muted);
  max-width: 30ch;
}

.footer-cols {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.footer-col {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.footer-label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 0.5rem;
}

.footer-col a,
.footer-col span {
  font-size: 0.87rem;
  color: rgba(237,230,216,0.55);
  transition: color var(--t);
}

.footer-col a:hover { color: var(--gold); }

.footer-bottom {
  max-width: var(--max-w);
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  padding: 1.5rem 0 2.5rem;
  font-size: 0.72rem;
  color: rgba(237,230,216,0.25);
  letter-spacing: 0.06em;
}

@media (max-width: 1024px) {
  .nav-links.desk { display: none; }
  .mobile-toggle  { display: flex; }

  .footer-top { grid-template-columns: 1fr; }
  .footer-brand { display: none; }
  .footer-cols { grid-template-columns: repeat(3, 1fr); }
}

@media (max-width: 600px) {
  #navbar { padding: 0 1.25rem; }
  .page-header { padding: 0 1.25rem 3rem; }
  .site-footer { padding: 3.5rem 1.25rem 0; }
  .footer-cols { grid-template-columns: 1fr 1fr; }
}
</style>
